import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { Button } from "../../components/ui/Button";
import { motion } from "framer-motion";
import bsnLogo from "../../assets/images/thirdPartyLogos/ucla-bsn.png";

interface CompetitionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onEnterCompetition: () => void;
}

const ModalWrapper = ({
  isOpen,
  onClose,
  onEnterCompetition,
}: {
  isOpen: boolean;
  onClose: () => void;
  onEnterCompetition: () => void;
}) => (
  <div
    className="fixed inset-0 overflow-y-auto overflow-x-hidden flex z-[99999]"
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      zIndex: 99999,
      padding: "1rem",
    }}
  >
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    />

    <div
      className="bg-white rounded-xl p-5 shadow-2xl max-w-sm w-full relative z-[100000] mx-4 my-auto"
      style={{
        position: "relative",
        zIndex: 100000,
        maxWidth: "48rem",
        width: "calc(100% - 2rem)",
        backgroundColor: "white",
        borderRadius: "0.75rem",
        padding: "2rem",
        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        marginTop: "calc(20vh)",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="grid grid-cols-[auto,1fr] gap-8">
        <div className="flex flex-col items-center space-y-6">
          <motion.div
            className="w-48 h-48 rounded-full flex items-center justify-center"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <img
              src={bsnLogo}
              alt="BSN Logo"
              className="w-32 h-32 object-contain"
            />
          </motion.div>
        </div>

        <div className="flex flex-col">
          <div className="text-left">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <h2 className="text-[32px] font-bold mb-2 tracking-tight flex items-center gap-3">
                <span
                  className="text-2xl"
                  style={{
                    color: "var(--color-primary)",
                  }}
                >
                  💼
                </span>
                UCLA Case Competition
              </h2>
              <p className="text-gray-600 text-base mb-6">
                Hosted by Bruin Strategy Network at UCLA
              </p>
            </motion.div>
          </div>

          <motion.div
            className="space-y-5"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <p className="text-2xl font-semibold text-gray-800">
              Welcome to BSN x WeKruit Mock Case Competition 🎉
            </p>
            <div className="text-base text-gray-600">
              <p className="font-semibold mb-4 text-gray-800 text-lg">
                ✨ Why Participate?
              </p>
              <div className="grid grid-cols-1 gap-3">
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100 transition-all duration-300 hover:scale-[1.02] hover:shadow-md flex items-start gap-3">
                  <span className="text-blue-600 text-xl">💡</span>
                  <span className="text-base">
                    Solve real-world consulting cases from top firms
                  </span>
                </div>
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100 transition-all duration-300 hover:scale-[1.02] hover:shadow-md flex items-start gap-3">
                  <span className="text-blue-600 text-xl">🏆</span>
                  <span className="text-base">
                    Top scorers win up to <strong>$100</strong>
                  </span>
                </div>
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100 transition-all duration-300 hover:scale-[1.02] hover:shadow-md flex items-start gap-3">
                  <span className="text-blue-600 text-xl">🌟</span>
                  <span className="text-base">
                    Get feedback from experienced consultants
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-6">
              <Button
                variant="outline"
                onClick={onClose}
                className="min-w-[140px] h-11 text-sm hover:bg-gray-100 transition-all duration-300"
                style={{
                  color: "var(--color-primary)",
                  borderColor: "var(--color-primary)",
                }}
              >
                Explore First
              </Button>
              <Button
                onClick={onEnterCompetition}
                className="min-w-[140px] h-11 text-sm"
                style={{
                  backgroundColor: "var(--color-primary)",
                  color: "white",
                }}
              >
                Enter Competition
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  </div>
);

const CompetitionDialog = React.memo(function CompetitionDialog({
  isOpen,
  onClose,
  onEnterCompetition,
}: CompetitionDialogProps) {
  useEffect(() => {
    if (isOpen) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {typeof document !== "undefined" &&
        ReactDOM.createPortal(
          <ModalWrapper
            isOpen={isOpen}
            onClose={onClose}
            onEnterCompetition={onEnterCompetition}
          />,
          document.body
        )}
    </>
  );
});

export default CompetitionDialog;
